import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PERMISSIONS,
  SupplierPaymentStatus,
  type SupplierPaymentAllocationSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useSupplierPaymentQuery, useVoidSupplierPaymentMutation } from '../api/payables.api';
import { SupplierPaymentStatusBadge } from '../components/PayableBadges';

function Detail({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One supplier payment: what left, against which invoices.
 *
 * Read-only, because a payment records something that already happened at a bank or a cash drawer.
 * There is no edit and no delete — a mistake is voided, which leaves both the payment and its
 * correction in the history where an auditor can see what happened.
 *
 * The provider's own reference shows here, on the document, to a caller the server allowed to read
 * it. It never travels in a URL, and never reaches storage or a log.
 */
export function SupplierPaymentDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { paymentId } = useParams<{ paymentId: string }>();
  const { can } = usePermissions();
  const { format } = useCurrency();

  const [isVoiding, setIsVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = useSupplierPaymentQuery(paymentId);
  const voidPayment = useVoidSupplierPaymentMutation();

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('payables.payments.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const payment = data;
  const isPosted = payment.status === SupplierPaymentStatus.POSTED;
  // The same capability records a payment and corrects one: voiding is how a mis-entry is fixed.
  const canVoid = isPosted && can(PERMISSIONS.PAYABLES_PAY);

  const confirmVoid = () => {
    if (!reason.trim()) return;
    voidPayment.mutate(
      { id: payment.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setFeedback(t('payables.void.done', { reference: payment.reference }));
          setIsVoiding(false);
          setReason('');
        },
      },
    );
  };

  const allocationColumns: Column<SupplierPaymentAllocationSummary>[] = [
    {
      key: 'reference',
      header: t('payables.fields.reference'),
      primary: true,
      render: (allocation) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {allocation.reference}
          </span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {allocation.supplierInvoiceNumber}
          </span>
        </span>
      ),
    },
    {
      key: 'invoiceDate',
      header: t('payables.fields.invoiceDate'),
      render: (allocation) => (
        <span className="text-ink text-sm" dir="ltr">
          {allocation.invoiceDate}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: t('payables.fields.dueDate'),
      render: (allocation) => (
        <span className="text-ink text-sm" dir="ltr">
          {allocation.dueDate}
        </span>
      ),
    },
    {
      key: 'allocated',
      header: t('payables.fields.allocatedAmount'),
      align: 'end',
      render: (allocation) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(allocation.allocatedMinor)}
        </span>
      ),
    },
    {
      key: 'outstanding',
      header: t('payables.fields.outstandingNow'),
      align: 'end',
      render: (allocation) => (
        <span className="text-muted" dir="ltr">
          {format(allocation.outstandingMinor)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (allocation) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('payables.openInvoiceNamed', { reference: allocation.reference })}
          onClick={() =>
            void navigate(`/finance/payables/invoices/${allocation.purchaseInvoiceId}`)
          }
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={payment.reference}
        subtitle={t('payables.payments.detailSubtitle', {
          supplier: payment.supplierName,
          date: payment.paymentDate,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/finance/payables/payments')}>
              {t('payables.backToPayments')}
            </Button>
            {canVoid ? (
              <Button variant="danger" onClick={() => setIsVoiding(true)}>
                {t('payables.void.action')}
              </Button>
            ) : null}
          </div>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {payment.status === SupplierPaymentStatus.VOIDED ? (
        <Alert tone="warning">
          <span className="flex flex-col gap-1">
            <span>
              {t('payables.void.notice', {
                reason: payment.voidedReason ?? '',
                by: payment.voidedByName ?? t('common.unknown'),
              })}
            </span>
            <span>{t('payables.void.notABankReversal')}</span>
          </span>
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('payables.payments.summarySection')}
          action={<SupplierPaymentStatusBadge status={payment.status} />}
        />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail label={t('payables.fields.supplier')} value={payment.supplierName} />
            <Detail label={t('payables.fields.branch')} value={payment.branchName} />
            <Detail label={t('payables.fields.currency')} value={payment.currencyCode} ltr />
            <Detail label={t('payables.fields.paymentDate')} value={payment.paymentDate} ltr />
            <Detail
              label={t('payables.fields.method')}
              value={t(`payables.methods.${payment.method}`)}
            />
            <Detail
              label={t('payables.fields.externalReference')}
              value={payment.externalReference ?? '—'}
              ltr
            />
            <Detail
              label={t('payables.fields.postedBy')}
              value={payment.postedByName ?? t('common.unknown')}
            />
            <Detail label={t('payables.fields.amount')} value={format(payment.amountMinor)} ltr />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('payables.payments.allocationsSection')}
          subtitle={t('payables.allocationCount', { count: payment.allocationCount })}
        />
        <DataTable
          caption={t('payables.payments.allocationsCaption')}
          columns={allocationColumns}
          rows={payment.allocations}
          rowKey={(allocation) => allocation.id}
          emptyTitle={t('payables.payments.noAllocations')}
        />
        <CardBody className="border-line space-y-2 border-t text-sm">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-ink font-medium">{t('payables.fields.amount')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(payment.amountMinor)}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.settlingNow')}</span>
            <span className="text-ink" dir="ltr">
              {format(payment.activeMinor)}
            </span>
          </div>
        </CardBody>
      </Card>

      <Modal
        isOpen={isVoiding}
        onClose={() => setIsVoiding(false)}
        title={t('payables.void.title')}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsVoiding(false)}
              disabled={voidPayment.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmVoid}
              isLoading={voidPayment.isPending}
              disabled={!reason.trim() || voidPayment.isPending}
            >
              {t('payables.void.action')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-ink text-sm">
            {t('payables.void.explain', { reference: payment.reference })}
          </p>
          {/* Said plainly, because someone reading this may believe the bank has been told. It has not. */}
          <ul className="text-muted list-disc space-y-1 text-sm ps-5">
            <li>{t('payables.void.stopsSettlement')}</li>
            <li>{t('payables.void.restoresOutstanding')}</li>
            <li>{t('payables.void.keepsHistory')}</li>
            <li className="text-ink font-medium">{t('payables.void.notABankReversal')}</li>
          </ul>
          {voidPayment.isError ? (
            <Alert tone="error">
              {apiErrorMessage(voidPayment.error) ?? t('payables.void.error')}
            </Alert>
          ) : null}
          <Input
            label={t('payables.fields.voidReason')}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
